import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { GiArchiveResearch } from 'react-icons/gi';
import { LiaChalkboardTeacherSolid } from 'react-icons/lia';
import { RiParentLine } from 'react-icons/ri';
import { FaUsersGear } from 'react-icons/fa6';

// import Logo from '../../../images/logo/logo.png';
import useUser from '../../../hooks/user';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { phases } from '../../../config/app';

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
}

const logoConfig = {
  p1: { src: 'logo_p1.png', className: 'h-30 w-40' },
  p2: { src: 'logo_p2.png', className: 'h-20 w-40' },
  p3: { src: 'logo_p3.png', className: 'h-30 w-40' },
  p4: { src: 'logo_p4.png', className: 'h-30 w-40' },
};

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const location = useLocation();
  const user = useUser('token');
  const [phaseRaw] = useLocalStorage('phase', '');

  const phase = (
    ['p1', 'p2', 'p3', 'p4'].includes(phaseRaw) ? phaseRaw : 'p1'
  ) as keyof typeof logoConfig;

  const logo = logoConfig[phase];
  const phaseConfig = phases.find((p) => p.key === phase);

  const { pathname } = location;

  const trigger = useRef<any>(null);
  const sidebar = useRef<any>(null);

  const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
  const [sidebarExpanded, _] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === 'true',
  );

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', sidebarExpanded.toString());
    if (sidebarExpanded) {
      document.querySelector('body')?.classList.add('sidebar-expanded');
    } else {
      document.querySelector('body')?.classList.remove('sidebar-expanded');
    }
  }, [sidebarExpanded]);

  return (
    <aside
      ref={sidebar}
      className={`absolute left-0 top-0 z-9999 flex h-screen w-72.5 flex-col overflow-y-hidden bg-black duration-300 ease-linear dark:bg-boxdark lg:static lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      {/* <!-- SIDEBAR HEADER --> */}
      <div className="flex items-center justify-between gap-2 px-6 py-5.5 lg:py-6.5">
        <NavLink
          to="/"
          className="flex flex-col items-center justify-center w-full gap-2"
        >
          {logo && (
            <img
              src={`../../../images/${logo.src}`}
              alt="Logo"
              className={`mb-2 ${logo.className}`}
            />
          )}

          <div className="text-white text-center ">
            <p>{phaseConfig?.label}</p>
            <p>{phaseConfig?.labelth}</p>
          </div>
        </NavLink>

        <button
          ref={trigger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-controls="sidebar"
          aria-expanded={sidebarOpen}
          className="block lg:hidden"
        >
          <svg
            className="fill-current"
            width="20"
            height="18"
            viewBox="0 0 20 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 8.175H2.98748L9.36248 1.6875C9.69998 1.35 9.69998 0.825 9.36248 0.4875C9.02498 0.15 8.49998 0.15 8.16248 0.4875L0.399976 8.3625C0.0624756 8.7 0.0624756 9.225 0.399976 9.5625L8.16248 17.4375C8.31248 17.5875 8.53748 17.7 8.76248 17.7C8.98748 17.7 9.17498 17.625 9.36248 17.475C9.69998 17.1375 9.69998 16.6125 9.36248 16.275L3.02498 9.8625H19C19.45 9.8625 19.825 9.4875 19.825 9.0375C19.825 8.55 19.45 8.175 19 8.175Z"
              fill=""
            />
          </svg>
        </button>
      </div>
      {/* <!-- SIDEBAR HEADER --> */}

      <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
        {/* <!-- Sidebar Menu --> */}
        <nav className="mt-5 py-4 px-4 lg:mt-1 lg:px-6">
          {/* <!-- Menu Group --> */}
          <div>
            <h3 className="mb-4 ml-4 text-sm font-semibold text-bodydark2">
              เมนู
            </h3>

            <ul className="mb-6 flex flex-col gap-1.5">
              {[5, 6].includes(user.role ?? 0) && (
                <>
                  <li>
                    <NavLink
                      to="/sornthai/admin/users"
                      className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                        pathname.includes('sornthai/admin') &&
                        'bg-graydark dark:bg-meta-4'
                      }`}
                    >
                      <FaUsersGear fontSize={24} />
                      บัญชีผู้ใช้งาน
                    </NavLink>
                  </li>
                </>
              )}

              {[4].includes(user.role ?? 0) && (
                <li>
                  <NavLink
                    to="/sornthai/dashboard-researcher"
                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                      pathname.includes('sornthai/dashboard-researcher') &&
                      'bg-graydark dark:bg-meta-4'
                    }`}
                  >
                    <GiArchiveResearch fontSize={24} />
                    นักวิจัย
                  </NavLink>
                </li>
              )}

              {[3,4].includes(user.role ?? 0) && (
                <li>
                  <NavLink
                    to="/sornthai/dashboard-teacher"
                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                      pathname.includes('sornthai/dashboard-teacher') &&
                      'bg-graydark dark:bg-meta-4'
                    }`}
                  >
                    <LiaChalkboardTeacherSolid fontSize={24} />
                    ครู
                  </NavLink>
                </li>
              )}

              {[2,4].includes(user.role ?? 0) && (
                <li>
                  <NavLink
                    to="/sornthai/dashboard-parent"
                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                      pathname.includes('sornthai/dashboard-parent') &&
                      'bg-graydark dark:bg-meta-4'
                    }`}
                  >
                    <RiParentLine fontSize={24} />
                    ผู้ปกครอง
                  </NavLink>
                </li>
              )}
            </ul>
          </div>
        </nav>
        {/* <!-- Sidebar Menu --> */}
      </div>
    </aside>
  );
};

export default Sidebar;
